import { useMemo } from "react";
import { TravelerEditMode, type TravellerFormSections } from "@apac/travelers";
import { useI18nContext } from "@hopper-b2b/i18n";

export interface UseTravellerFormOverridesParams {
  showGender?: boolean;
  showNationality?: boolean;
  showPassportDetails?: boolean;
  requireGender?: boolean;
  requireNationality?: boolean;
  requirePassportDetails?: boolean;
  travelerEditMode?: TravelerEditMode;
  showDriverLicenseDetails?: { showAddress: boolean; showCity: boolean };
}

export interface UseTravellerFormOverridesResult {
  travellerFormOverrides: TravellerFormSections;
}

export const useTravellerFormOverrides = (
  params: UseTravellerFormOverridesParams = {}
): UseTravellerFormOverridesResult => {
  const { t } = useI18nContext();
  const {
    showGender: showGenderProp = true,
    showNationality: showNationalityProp = false,
    showPassportDetails: showPassportDetailsProp,
    requireGender: requireGenderProp,
    requireNationality: requireNationalityProp,
    requirePassportDetails: requirePassportDetailsProp,
    travelerEditMode,
    showDriverLicenseDetails = { showAddress: false, showCity: false },
  } = params;

  const requireGender = requireGenderProp ?? showGenderProp;
  const requireNationality = requireNationalityProp ?? showNationalityProp;
  const requirePassportDetails =
    requirePassportDetailsProp ?? showPassportDetailsProp === true;

  const showGender = showGenderProp || requireGender;
  const showNationality = showNationalityProp || requireNationality;
  const showPassportDetails =
    showPassportDetailsProp === true || requirePassportDetails;

  const showDriverInfo =
    travelerEditMode === TravelerEditMode.CARS_ONLY ||
    travelerEditMode === TravelerEditMode.ALL;

  const { showAddress: showDriverAddress, showCity: showDriverCity } =
    showDriverLicenseDetails;

  const travellerFormOverrides = useMemo<TravellerFormSections>(
    () => ({
      travellerDetails: {
        firstName: { required: true },
        lastName: { required: true },
        dateOfBirth: {
          required: true,
          placeholder: t("checkoutSection.travelerForm.dateOfBirthPlaceholder"),
        },
        ...(showGender && requireGender ? { gender: { required: true } } : {}),
        ...(showNationality && requireNationality
          ? { nationality: { required: true } }
          : {}),
      },
      ...(showPassportDetails && requirePassportDetails
        ? {
            passportDetails: {
              nationality: { required: true },
              passportIssuanceCountry: { required: true },
              passportNumber: { required: true },
              passportExpiryDate: { required: true },
            },
          }
        : {}),
      ...(showDriverInfo
        ? {
            driverLicense: {
              ...(showDriverAddress ? { address: { required: true } } : {}),
              ...(showDriverCity ? { issuingCity: { required: true } } : {}),
              issuingCountry: { required: true },
              postalCode: { required: true },
            },
          }
        : {}),
    }),
    [
      t,
      showGender,
      requireGender,
      showNationality,
      requireNationality,
      showPassportDetails,
      requirePassportDetails,
      showDriverInfo,
      showDriverAddress,
      showDriverCity,
    ]
  );

  return { travellerFormOverrides };
};
